<template>
  <div>
    <CCard>
      <CCardHeader>
        Integration Sync Checks
      </CCardHeader>

      <CCardBody>
        <div
          v-if="isLoading"
          class="d-flex justify-content-center align-items-center py-5"
        >
          <CSpinner
            color="info"
            grow
          />
        </div>

        <CDataTable
          v-else
          small
          border
          sorter
          column-filter
          :column-filter-value.sync="columnFilterState"
          :items="items"
          :fields="fields"
          :sorter-value="{ column: 'checkTimestamp', asc: false }"
          :no-items-view="{ noResults: 'No results matching filters', noItems: 'No sync check data available' }"
        >
          <template #integrationType-filter>
            <select
              class="form-control form-control-sm"
              @change="onColumnFilter('integrationType', $event.target.value)"
            >
              <option value="">
                All
              </option>
              <option
                v-for="type in integrationTypes"
                :key="type"
                :value="type"
              >
                {{ formatPascalCase(type) }}
              </option>
            </select>
          </template>

          <template #integrationType="{item}">
            <td>
              {{ formatPascalCase(item.integrationType) }}
            </td>
          </template>

          <template #status-filter>
            <select
              class="form-control form-control-sm"
              @change="onColumnFilter('status', $event.target.value)"
            >
              <option value="">
                All
              </option>
              <option
                v-for="s in statuses"
                :key="s"
                :value="s"
              >
                {{ formatPascalCase(s) }}
              </option>
            </select>
          </template>

          <template #status="{item}">
            <td>
              <CBadge :color="statusColor(item.status)">
                {{ formatPascalCase(item.status) }}
              </CBadge>
            </td>
          </template>

          <template #checkTimestamp="{item}">
            <td>
              {{ formatTimestamp(item.checkTimestamp) }}
            </td>
          </template>

          <template #outOfSyncIds="{item}">
            <td>
              <template v-if="item.outOfSyncIds && item.outOfSyncIds.length">
                <CButton
                  size="sm"
                  color="info"
                  variant="ghost"
                  class="p-1"
                  @click="toggleExpand(item.id)"
                >
                  <CIcon
                    :name="expandedRows[item.id] ? 'cil-chevron-bottom' : 'cil-chevron-right'"
                    size="sm"
                    class="mr-1"
                  />
                  {{ expandedRows[item.id] ? 'Hide' : 'Show' }} ({{ item.outOfSyncIds.length }})
                </CButton>
              </template>
              <span v-else>-</span>
            </td>
          </template>

          <template #errorMessage="{item}">
            <td style="max-width: 200px">
              <div
                v-if="item.errorMessage"
                class="error-message-cell"
              >
                <span
                  class="error-message-text"
                  :title="item.errorMessage"
                >{{ item.errorMessage }}</span>
                <CButton
                  size="sm"
                  color="secondary"
                  variant="ghost"
                  class="copy-btn"
                  title="Copy error message"
                  @click="copyToClipboard(item.errorMessage)"
                >
                  <CIcon
                    name="cil-copy"
                    size="sm"
                  />
                </CButton>
              </div>
              <span v-else>-</span>
            </td>
          </template>

          <template #actions="{item}">
            <td>
              <CButton
                color="info"
                size="sm"
                @click="confirmResync(item)"
              >
                Trigger Resync
              </CButton>
            </td>
          </template>

          <template #details="{item}">
            <div
              v-if="item.outOfSyncIds && item.outOfSyncIds.length && expandedRows[item.id]"
              class="sync-ids-details"
            >
              <table class="table table-sm table-bordered mb-0">
                <thead class="table-secondary">
                  <tr>
                    <th>ID</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="syncId in item.outOfSyncIds"
                    :key="syncId"
                  >
                    <td>
                      <router-link
                        v-if="getRouteForSyncId(item.integrationType, syncId)"
                        :to="getRouteForSyncId(item.integrationType, syncId)"
                        target="_blank"
                      >
                        {{ syncId }}
                        <CIcon
                          name="cil-external-link"
                          size="custom"
                          class="ml-1"
                        />
                      </router-link>
                      <span v-else>{{ syncId }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </template>
        </CDataTable>
      </CCardBody>
    </CCard>

    <CModal
      title="Confirm Trigger Resync"
      color="warning"
      :show.sync="showResyncModal"
    >
      Are you sure you want to trigger a resync for
      <strong>{{ resyncItem ? formatPascalCase(resyncItem.integrationType) : '' }}</strong>?

      <template #footer>
        <ButtonLoading
          color="primary"
          :loading="isResyncLoading"
          @click="onConfirmResync"
        >
          Trigger Resync
        </ButtonLoading>
        <CButton
          color="danger"
          @click="showResyncModal = false"
        >
          Cancel
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import IntegrationSyncCheckApi from '@/api/integration-sync-check.api'
import SyncApi from '@/api/sync.api'
import { showErrorMessage, showSuccessMessage } from '@/notification-utils'
import ButtonLoading from '@/components/common/ButtonLoading.vue'

export default {
  name: 'IntegrationSyncChecks',

  components: {
    ButtonLoading
  },

  title: 'Integration Sync Checks',

  data() {
    return {
      isLoading: false,
      isResyncLoading: false,
      showResyncModal: false,
      resyncItem: null,
      items: [],
      expandedRows: {},
      columnFilterState: {},
      integrationTypes: [
        'BuyerCampaign',
        'Category',
        'SellerOfferRegistration'
      ],
      statuses: [
        'AllInSync',
        'SomeOutOfSync',
        'Failed'
      ]
    }
  },

  computed: {
    fields() {
      return [
        { key: 'integrationType', label: 'Integration Type', sorter: true },
        { key: 'status', label: 'Status', sorter: true },
        { key: 'totalChecked', label: 'Total Checked', sorter: true, filter: false },
        { key: 'inSyncCount', label: 'In Sync', sorter: true, filter: false },
        { key: 'outOfSyncCount', label: 'Out of Sync', sorter: true, filter: false },
        { key: 'unknownCount', label: 'Unknown', sorter: true, filter: false },
        { key: 'checkTimestamp', label: 'Check Timestamp', sorter: true, filter: false },
        { key: 'outOfSyncIds', label: 'Out of Sync IDs', sorter: false, filter: false, _style: 'min-width: 180px' },
        { key: 'errorMessage', label: 'Error Message', sorter: false, _style: 'max-width: 200px' },
        { key: 'actions', label: 'Actions', sorter: false, filter: false }
      ]
    }
  },

  async created() {
    await this.loadData()
  },

  methods: {
    async loadData() {
      this.isLoading = true
      try {
        const response = await IntegrationSyncCheckApi.getAll()
        this.items = response.data
      } catch (error) {
        showErrorMessage('Failed to load sync check data')
      } finally {
        this.isLoading = false
      }
    },

    onColumnFilter(column, value) {
      this.columnFilterState = { ...this.columnFilterState, [column]: value }
    },

    formatPascalCase(value) {
      if (!value) {
        return '-'
      }

      return value.replace(/([A-Z])/g, ' $1').trim()
    },

    statusColor(status) {
      const colors = {
        AllInSync: 'success',
        SomeOutOfSync: 'warning',
        Failed: 'danger'
      }
      return colors[status] || 'secondary'
    },

    formatTimestamp(timestamp) {
      if (!timestamp) {
        return '-'
      }

      return new Date(timestamp).toLocaleString()
    },

    toggleExpand(id) {
      this.$set(this.expandedRows, id, !this.expandedRows[id])
    },

    confirmResync(item) {
      this.resyncItem = item
      this.showResyncModal = true
    },

    async onConfirmResync() {
      try {
        this.isResyncLoading = true
        await SyncApi.triggerResync(this.resyncItem.integrationType)
        this.showResyncModal = false
        showSuccessMessage(`Resync triggered for ${this.formatPascalCase(this.resyncItem.integrationType)}`)
        await this.loadData()
      } catch (error) {
        showErrorMessage(`Failed to trigger resync for ${this.formatPascalCase(this.resyncItem.integrationType)}`)
      } finally {
        this.isResyncLoading = false
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        showSuccessMessage('Copied to clipboard')
      } catch {
        showErrorMessage('Failed to copy to clipboard')
      }
    },

    getRouteForSyncId(integrationType, syncId) {
      const routeMap = {
        BuyerCampaign: { name: 'BuyerCampaignDetails', params: { id: syncId } },
        Buyer: { name: 'BuyerAccountDetails', params: { id: syncId } },
        Seller: { name: 'SellerAccountDetails', params: { id: syncId } },
        Category: { name: 'CategoryEdit', params: { id: syncId } },
        SellerOfferRegistration: { name: 'SellerOfferRegistrationDetails', params: { registrationId: syncId } }
      }
      return routeMap[integrationType] || null
    }
  }
}
</script>

<style scoped>
.error-message-cell {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.error-message-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
}

.copy-btn {
  flex-shrink: 0;
  padding: 0.15rem 0.3rem;
}

.sync-ids-details {
  background-color: #ffffff;
  padding: 0.75rem;
  border-radius: 0.25rem;
  border: 1px solid #dee2e6;
  margin-top: 0.5rem;
}
</style>
